import { useEffect, useRef, useState } from "react";
import { API_URL } from "../api/auth";
import { useAuthToken } from "./use-auth-token";

export type SSEEvent<TData = Record<string, unknown>> = {
  id: string;
  event: string;
  data: TData & { ts: string };
};

export type SSEHookOptions<TData = Record<string, unknown>> = {
  onEvent?: (event: SSEEvent<TData>) => void;
  tokenOverride?: string;
  /**
   * Enable automatic reconnection on disconnect (default: true)
   */
  autoReconnect?: boolean;
  /**
   * Pause connection when page is hidden (default: true)
   * Uses Page Visibility API to save resources
   */
  pauseWhenHidden?: boolean;
};

export type SSEHookReturn<TData = Record<string, unknown>> = {
  events: SSEEvent<TData>[];
  isConnected: boolean;
  error: string | null;
  reconnect: () => void;
};

/**
 * Hook for consuming Server-Sent Events (SSE) with type safety and automatic authentication.
 *
 * Features:
 * - Type-safe event data with automatic `ts` field
 * - Automatic token handling via Authorization header (Clerk token or API key)
 * - Events sorted by timestamp (newest first)
 * - Optional event callback
 * - Connection state management with automatic reconnection
 * - Smart connection pausing when page is hidden (saves resources)
 * - Exponential backoff for reconnection attempts
 * - Uses fetch API with custom headers (not EventSource)
 *
 * @example
 * ```tsx
 * type MyEventData = { a: string; b: number };
 * const { events, isConnected } = useSSE<MyEventData>('/app-events', {
 *   onEvent: (event) => {
 *     console.log(event.data.ts);  // Always available
 *     console.log(event.data.a);   // Type-safe access
 *   },
 *   autoReconnect: true,
 *   pauseWhenHidden: true,
 * });
 * ```
 */
export const useSSE = <TData = Record<string, unknown>>(
  path: string,
  options?: SSEHookOptions<TData>,
): SSEHookReturn<TData> => {
  const finalToken = useAuthToken(options?.tokenOverride);
  const [events, setEvents] = useState<SSEEvent<TData>[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [_reconnectTrigger, setReconnectTrigger] = useState(0);
  const [isPageVisible, setIsPageVisible] = useState(!document.hidden);
  const abortControllerRef = useRef<AbortController | null>(null);
  const onEventRef = useRef(options?.onEvent);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const autoReconnect = options?.autoReconnect !== false;
  const pauseWhenHidden = options?.pauseWhenHidden !== false;

  // Clear events when path changes (e.g., navigating to different resource)
  // biome-ignore lint/correctness/useExhaustiveDependencies: path is a hook parameter, not an outer scope value
  useEffect(() => {
    setEvents([]);
    setError(null);
  }, [path]);

  // Keep onEvent ref updated
  useEffect(() => {
    onEventRef.current = options?.onEvent;
  }, [options?.onEvent]);

  // Page Visibility API handler
  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsPageVisible(!document.hidden);
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  // Main SSE connection effect
  useEffect(() => {
    // Don't connect if we don't have a token
    if (!finalToken) {
      setIsConnected(false);
      setError("No authentication token available");
      return;
    }

    // Don't connect if page is hidden and pauseWhenHidden is enabled
    if (pauseWhenHidden && !isPageVisible) {
      console.log("[SSE] Page is hidden, skipping connection");
      return;
    }

    // Clean up previous connection
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    // Clear any pending reconnection
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    // Create new AbortController for this connection
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    // Build URL
    const cleanPath = path.replace(/^\/+/, "");
    const url = `${API_URL}/${cleanPath}`;

    // Calculate backoff delay (exponential with max 30s)
    const getBackoffDelay = (attempts: number) => {
      const baseDelay = 1000; // 1 second
      const maxDelay = 30000; // 30 seconds
      const delay = Math.min(baseDelay * 2 ** attempts, maxDelay);
      return delay;
    };

    // Start SSE connection using fetch with headers
    const connect = async () => {
      try {
        console.log(`[SSE] Connecting to ${url} (attempt ${reconnectAttemptsRef.current + 1})`);

        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${finalToken}`,
            Accept: "text/event-stream",
          },
          signal: abortController.signal,
        });

        if (!response.ok) {
          const body = await response.json().catch(() => null);
          const detail = body?.detail;
          const message =
            typeof detail === "string"
              ? detail
              : (body?.message ?? `HTTP error! status: ${response.status}`);
          throw new Error(message);
        }

        if (!response.body) {
          throw new Error("Response body is null");
        }

        // Reset reconnect attempts on successful connection
        reconnectAttemptsRef.current = 0;
        setIsConnected(true);
        setError(null);

        console.log("[SSE] Connected successfully");

        // Read the stream
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        // Current event being parsed
        let currentEvent: {
          id?: string;
          event?: string;
          data?: string;
        } = {};

        while (true) {
          const { done, value } = await reader.read();

          if (done) {
            console.log("[SSE] Stream ended");
            setIsConnected(false);

            // Auto-reconnect if enabled and page is visible
            if (autoReconnect && (!pauseWhenHidden || isPageVisible)) {
              const delay = getBackoffDelay(reconnectAttemptsRef.current);
              console.log(`[SSE] Reconnecting in ${delay}ms...`);

              reconnectTimeoutRef.current = setTimeout(() => {
                reconnectAttemptsRef.current += 1;
                setReconnectTrigger((prev) => prev + 1);
              }, delay);
            }
            break;
          }

          // Decode chunk and add to buffer
          buffer += decoder.decode(value, { stream: true });

          // Process complete lines
          const lines = buffer.split("\n");
          buffer = lines.pop() || ""; // Keep incomplete line in buffer

          for (const line of lines) {
            // Empty line signals end of event
            if (line.trim() === "") {
              if (currentEvent.data) {
                try {
                  // Parse the JSON data
                  const parsedData = JSON.parse(currentEvent.data) as TData & {
                    ts: string;
                  };

                  const eventName = currentEvent.event || "message";
                  const sseEvent: SSEEvent<TData> = {
                    id: currentEvent.id || "",
                    event: eventName,
                    data: parsedData,
                  };

                  // Add to events list, deduplicate, and sort by timestamp (newest first)
                  setEvents((prev) => {
                    // Skip if event with same ID already exists (for events with IDs)
                    if (sseEvent.id && prev.some((e) => e.id === sseEvent.id)) {
                      return prev;
                    }
                    // For events without ID (like session_ended), only keep one per event type
                    if (!sseEvent.id && prev.some((e) => !e.id && e.event === eventName)) {
                      return prev;
                    }
                    const updated = [...prev, sseEvent];
                    return updated.sort(
                      (a, b) => new Date(b.data.ts).getTime() - new Date(a.data.ts).getTime(),
                    );
                  });

                  // Call the callback if provided
                  onEventRef.current?.(sseEvent);
                } catch (err) {
                  console.error("[SSE] Error parsing event data:", err);
                  setError(
                    `Failed to parse event: ${err instanceof Error ? err.message : String(err)}`,
                  );
                }
              }

              // Reset current event
              currentEvent = {};
              continue;
            }

            // Parse SSE field
            const colonIndex = line.indexOf(":");
            if (colonIndex === -1) continue;

            const field = line.slice(0, colonIndex).trim();
            const value = line.slice(colonIndex + 1).trim();

            switch (field) {
              case "id":
                currentEvent.id = value;
                break;
              case "event":
                currentEvent.event = value;
                break;
              case "data":
                currentEvent.data = value;
                break;
              // Ignore other fields like 'retry'
            }
          }
        }
      } catch (err) {
        if (err instanceof Error && err.name === "AbortError") {
          // Connection was aborted intentionally
          console.log("[SSE] Connection aborted");
          setIsConnected(false);
          return;
        }

        console.error("[SSE] Connection error:", err);
        setIsConnected(false);
        setError(err instanceof Error ? err.message : "Unknown connection error");

        // Auto-reconnect on error if enabled and page is visible
        if (autoReconnect && (!pauseWhenHidden || isPageVisible)) {
          const delay = getBackoffDelay(reconnectAttemptsRef.current);
          console.log(`[SSE] Reconnecting after error in ${delay}ms...`);

          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttemptsRef.current += 1;
            setReconnectTrigger((prev) => prev + 1);
          }, delay);
        }
      }
    };

    connect();

    // Cleanup
    return () => {
      console.log("[SSE] Cleaning up connection");
      abortController.abort();
      abortControllerRef.current = null;

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      setIsConnected(false);
    };
  }, [finalToken, path, isPageVisible, autoReconnect, pauseWhenHidden]);

  const reconnect = () => {
    // Reset reconnect attempts on manual reconnect
    reconnectAttemptsRef.current = 0;

    // Clear any pending reconnection
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    setReconnectTrigger((prev) => prev + 1);
    setError(null);
  };

  return {
    events,
    isConnected,
    error,
    reconnect,
  };
};
